import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import NavBar from "./NavBar";

/**
 * @typedef {Object} Props
 * @property {string} [text]
 * @property {string} [subtext]
 * @property {number} [dotSize]
 * @property {number} [dotSpacing]
 * @property {string} [dotColor]
 */

const CanvasBackground = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.8;
  pointer-events: none;
  z-index: 1;
  display: block;
`;

const HeroContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${(props) => (props.theme === "light" ? "#fff" : "#000")};
  transition: background 0.3s ease;
  z-index: 1;
  isolation: isolate;

  /* Fix for mobile browsers */
  @supports (-webkit-touch-callout: none) {
    min-height: -webkit-fill-available;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at center,
      ${(props) =>
        props.theme === "light"
          ? "rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%"
          : "rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%"}
    );
    z-index: 1;
    pointer-events: none;
    transition: background 0.3s ease;
  }
`;

const CursorFollower = styled(motion.div)`
  position: fixed;
  width: 1200px;
  height: 1200px;
  background: radial-gradient(
    circle at center,
    rgba(244, 144, 151, 0.15) 0%,
    rgba(234, 190, 0, 0.12) 20%,
    rgba(244, 144, 151, 0.08) 40%,
    rgba(0, 0, 0, 0) 100%
  );
  border-radius: 50%;
  pointer-events: none;
  z-index: 2;
  transform-origin: center center;
  mix-blend-mode: screen;
  filter: blur(50px);
  opacity: 0.8;
`;

const Content = styled(motion.div)`
  text-align: center;
  z-index: 3;
  color: ${(props) => (props.theme === "light" ? "#000" : "#fff")};
  position: relative;
  max-width: 1200px;
  padding: 0 20px;
  padding-top: 64px;
  transition: color 0.3s ease;
  font-family: lexend;

  h1 {
    font-size: clamp(3rem, 7vw, 4rem);
    font-weight: bold;
    margin-bottom: 2rem;
    line-height: 1.2;
  }

  p {
    font-size: clamp(1.2rem, 2vw, 1.8rem);
    color: ${(props) => (props.theme === "light" ? "#666" : "#999")};
    max-width: 800px;
    margin: 0 auto 3rem;
    line-height: 1.6;
    transition: color 0.3s ease;
  }
`;

const GradientText = styled(motion.span)`
  background: linear-gradient(
    to right,
    #f49097 0%,
    #eabe00 15%,
    #bc3627 30%,
    #eabe00 45%,
    #5c25d2 60%,
    #2aefc3 70%,
    #f49097 80%,
    #eabe00 90%,
    #f49097 95%,
    #eabe00 100%
  );
  font-family: lexend;
  background-size: 400% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shine 12s linear infinite;
  display: inline-block;
  font-weight: 600;
  letter-spacing: -0.02em;

  @keyframes shine {
    from {
      background-position: 0% center;
    }
    to {
      background-position: 400% center;
    }
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const Button = styled(motion.button)`
  padding: 15px 35px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  border: 2px solid #eabe00;
  font-family: lexend;
  cursor: pointer;
  position: relative;
  background: transparent;
  transition: all 0.3s ease;
  z-index: 1;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: all 0.3s ease;
    z-index: -1;
    border-radius: 50px;
  }
`;

const PrimaryButton = styled(Button)`
  color: #000;

  &::before {
    background: linear-gradient(135deg, #f49097 0%, #eabe00 100%);
  }

  &:hover {
    background: linear-gradient(135deg, #f49097 0%, #eabe00 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &:hover::before {
    background: ${(props) => (props.theme === "light" ? "#fff" : "#000")};
    opacity: 0;
  }
`;

const SecondaryButton = styled(Button)`
  color: #000;

  &::before {
    background: linear-gradient(135deg, #eabe00 0%, #f49097 100%);
  }

  &:hover {
    background: linear-gradient(135deg, #f49097 0%, #eabe00 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &:hover::before {
    // background: #000; /* Background becomes black */
    opacity: 0;
    background: ${(props) => (props.theme === "light" ? "#fff" : "#000")};
  }
`;

function drawDots(
  ctx,
  width,
  height,
  spacing = 30,
  dotSize = 1.5,
  time = 0,
  mousePos,
  mouseTrail,
  theme
) {
  ctx.clearRect(0, 0, width, height);

  const interpolateColor = (percent) => {
    const color1 =
      theme === "light"
        ? { r: 244, g: 144, b: 151 }
        : { r: 244, g: 144, b: 151 };
    const color2 =
      theme === "light" ? { r: 234, g: 190, b: 0 } : { r: 234, g: 190, b: 0 };

    const r = Math.floor(color1.r + (color2.r - color1.r) * percent);
    const g = Math.floor(color1.g + (color2.g - color1.g) * percent);
    const b = Math.floor(color1.b + (color2.b - color1.b) * percent);

    return `rgb(${r}, ${g}, ${b})`;
  };

  for (let x = 0; x < width + spacing; x += spacing) {
    for (let y = 0; y < height + spacing; y += spacing) {
      const baseSize = dotSize;

      let maxInfluence = 0;
      mouseTrail.forEach((point) => {
        const dx = x - point.x;
        const dy = y - point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const influence =
          Math.max(0, 1 - distance / 150) * (1 - point.age / 20);
        maxInfluence = Math.max(maxInfluence, influence);
      });

      const alpha = 0.3 + maxInfluence * 0.7;
      const size = baseSize * (1 + maxInfluence * 0.8);
      const color = interpolateColor(maxInfluence);

      ctx.beginPath();
      ctx.fillStyle = `${color
        .replace("rgb", "rgba")
        .replace(")", `, ${alpha})`)}`;
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default function HeroSectionAnimated({
  text = "Collaborate to build the future of AI",
  subtext = "Solve real-world problems and earn rewards while advancing your career",
  dotSize = 1.5,
  dotSpacing = 20,
  dotColor = "rgba(255, 255, 255, 0.3)",
}) {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? "dark" : "light";

  const canvasRef = React.useRef(null);
  // const animationFrameRef = React.useRef();
  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = React.useState(false);
  const mouseTrailRef = React.useRef([]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationTime = 0;
    let frameId;

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      // Set canvas size to match parent element
      const width = parent.offsetWidth;
      const height = parent.offsetHeight;
      const dpr = window.devicePixelRatio || 1;

      // Update canvas size with device pixel ratio
      canvas.width = width * dpr;
      canvas.height = height * dpr;

      // Update canvas style dimensions
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Scale the context to account for the device pixel ratio
      ctx.scale(dpr, dpr);
    };

    // Initial resize
    resizeCanvas();

    const animate = () => {
      mouseTrailRef.current = mouseTrailRef.current
        .map((point) => ({ ...point, age: point.age + 1 }))
        .filter((point) => point.age < 20);

      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);

      drawDots(
        ctx,
        width,
        height,
        dotSpacing,
        dotSize,
        animationTime,
        mousePos,
        mouseTrailRef.current,
        theme
      );

      animationTime += 1;
      frameId = requestAnimationFrame(animate);
    };

    // Handle resize events
    const debouncedResize = debounce(resizeCanvas, 250);
    window.addEventListener("resize", debouncedResize);

    // Start animation
    animate();

    return () => {
      window.removeEventListener("resize", debouncedResize);
      cancelAnimationFrame(frameId);
    };
  }, [dotSize, dotSpacing, mousePos, theme]);

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setMousePos({ x, y });
      mouseTrailRef.current.push({ x, y, age: 0 });

      if (mouseTrailRef.current.length > 20) {
        mouseTrailRef.current.shift();
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const handleExploreClick = () => {
    const challengesSection = document.getElementById("why-join");
    if (challengesSection) {
      const yOffset = -80; // Adjust this value for header height/padding
      const y =
        challengesSection.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }
  };

  return (
    <HeroContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      theme={theme}
    >
      <NavBar isTransparent={true} />
      <CanvasBackground ref={canvasRef} />
      <CursorFollower
        animate={{
          x: mousePos.x,
          y: mousePos.y,
          scale: isHovered ? 1.2 : 1,
          opacity: isHovered ? 0.8 : 0.6,
        }}
        initial={{
          scale: 0,
          opacity: 0,
        }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          transform: `translate(-50%, -50%)`,
          pointerEvents: "none",
        }}
        transition={{
          type: "spring",
          damping: 40,
          stiffness: 150,
          mass: 1,
        }}
      />
      <Content
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        theme={theme}
      >
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <GradientText>{text}</GradientText>
        </motion.h1>
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {subtext}
        </motion.p>
        <ButtonContainer
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <PrimaryButton
            onClick={() =>
              (window.location.href = "https://app.lunor.quest/")
            }
            theme={theme}
          >
            Join Quest
          </PrimaryButton>
          <SecondaryButton onClick={handleExploreClick} theme={theme}>
            Explore
          </SecondaryButton>
        </ButtonContainer>
      </Content>
    </HeroContainer>
  );
}
